import React, { useState, useEffect, Fragment } from 'react';
import { useTranslations } from '../../localization/useTranslations';
import { LoginForm } from '../../auth/components/LoginForm';
import Footer from '../../payment/components/PaymentPageFooter';
import ActivationHeader from './ActivationHeader';
import UserCard from './UserCard';
import AddressForm from './AddressForm';
import Button from '../../components/buttons/SimpleButton/Button';
import AddressCard from '../components/AddressCard';
import { AddressResponse, Address } from '../types/Address';
import { FamilyActivationResponse } from '../types/CodeInfo';
import { getAddresses, getCodeInfo, activateCode } from '../services/ActivationService';
import { FamilyCodeInfoResponse } from '../types/CodeInfo';
import FinishPage from './FinishPage';
import { isUserLoggedIn } from '../../common/Auth';
import CustomSnackbar from '../../components/CustomSnackbar';
import { logout } from '../../auth/services/AuthService';
import { getDeliveryAddressType } from '../../utils/getDeliveryAddressType';
import Dialog from '../../common/components/Dialog';
import Icon from '../../common/components/Icon';

// Main component for subscription activation
const SubscriptionActivation = () => {
  // Fetch translations based on the provided country or use default
  const translations = useTranslations();
  const country = window.country?.toLowerCase() || 'cz';

  // State variables
  const [codeInfo, setCodeInfo] = useState<FamilyCodeInfoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [printAddressData, setPrintAddressData] = useState<Address | null>(null);
  const [state, setState] = useState<'invalidCode' | 'login' | 'userCard' | 'addressForm' | 'addressCard' | 'finish'>(
    'login'
  );
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [errorStatus, setErrorStatus] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<'web' | 'print' | 'both'>('print');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const currentCode = urlParams.get('code');

  const featureCodes = window?.forbes_sales_funnel?.list_of_features?.map((feature) => feature.feature_code) || [];

  useEffect(() => {
    if (codeInfo) {
      const contentAccess = codeInfo?.subscription?.content_access || [];

      if (contentAccess.length > 0) {
        // Determine subscription type based on content access
        if (contentAccess.includes('web') && contentAccess.includes('print')) {
          setSubscriptionType('both');
        } else if (contentAccess.includes('web')) {
          setSubscriptionType('web');
        } else if (contentAccess.includes('print')) {
          setSubscriptionType('print');
        }
      }
    }
  }, [codeInfo]);

  // Handle button click on the user card
  const handleUserCardButtonClick = async () => {
    if (codeInfo && codeInfo.subscription.content_access) {
      if (codeInfo.subscription.content_access.includes('print')) {
        setState(printAddressData ? 'addressCard' : 'addressForm');
      } else if (codeInfo.subscription.content_access.includes('web')) {
        try {
          setLoading(true);

          await activateCode(currentCode!)
            .then((activationResponse: FamilyActivationResponse) => {
              if (!activationResponse.code || !activationResponse.subscription) {
                setErrorStatus(true);
              }

              setState('finish');
            })
            .catch((error) => {
              updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during activation');
              setErrorStatus(true);
              setState('finish');
            });
        } catch (error) {
          updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during activation');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (snackbarOpen) {
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 6000);
    }
  }, [snackbarOpen]);

  // Handle button click on the address card
  const handleAddressCardButtonClick = async () => {
    try {
      setLoading(true);

      await activateCode(currentCode!)
        .then((activationResponse: FamilyActivationResponse) => {
          if (!activationResponse.code || !activationResponse.subscription) {
            updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during activation');
            setErrorStatus(true);
          }
          setState('finish');
        })
        .catch((error) => {
          updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during activation');
          setErrorStatus(true);
          setState('finish');
        });
    } catch (error) {
      updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during activation');
    } finally {
      setLoading(false);
    }
  };

  const updateSnackbarMessage = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Handle button click on the address form
  const handleAddressFormSubmit = (updatedAddressData: Address) => {
    setPrintAddressData(updatedAddressData);
    setState('addressCard');
  };

  // Handle button click to edit address
  const handleEditAddressClick = () => {
    setState('addressForm');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Logout error');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const nToken = isUserLoggedIn();

        if (nToken) {
          setState('userCard');
        } else {
          setState('login');
        }

        if (currentCode) {
          getCodeInfo(currentCode).then((infoData) => {
            setCodeInfo(infoData);
            if (infoData.statusCode !== 200) {
              setState('invalidCode');
            }
          });
        } else {
          setState('invalidCode');
        }

        const email = window.crm_user?.user?.email || '';
        const deliveryType = getDeliveryAddressType(country);

        getAddresses(email).then(async (addressData: AddressResponse) => {
          let deliveryAddress = null;

          for (const key in addressData.addresses) {
            if (Object.prototype.hasOwnProperty.call(addressData.addresses, key)) {
              const address = addressData.addresses[key];
              if (address && address.type === deliveryType) {
                deliveryAddress = address;
                break;
              }
            }
          }

          setPrintAddressData(deliveryAddress);
        });
      } catch (error) {
        updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during data fetching');
      }
    };

    fetchData();
  }, [country, currentCode, translations?.snackBarMessages?.somethingWentWrong]);

  const renderState = () => {
    switch (state) {
      case 'invalidCode':
        return invalidCodeMarkup;
      case 'login':
        return loginMarkup;
      case 'userCard':
        return userCardMarkup;
      case 'addressForm':
        return addressFormMarkup;
      case 'addressCard':
        return addressCardMarkup;
      case 'finish':
        return (
          <FinishPage
            errorStatus={errorStatus}
            subscriptionType={subscriptionType}
            featureCodes={featureCodes}
            updateSnackbarMessage={updateSnackbarMessage}
          />
        );
      default:
        return loginMarkup;
    }
  };

  // JSX markup for the login state
  const loginMarkup = (
    <div className="default-state default-state--logged-out">
      <ActivationHeader state={state} codeInfo={codeInfo} />
      <LoginForm isFormInModal withResetPasswordModal />
    </div>
  );

  // JSX markup for the user card state
  const userCardMarkup = (
    <div className="default-state default-state--logged-in">
      <ActivationHeader state={state} codeInfo={codeInfo} />
      <UserCard updateSnackbarMessage={updateSnackbarMessage} />
      <div className="page-subscription-activation__buttons-wrapper">
        <Button
          className=""
          type="primary"
          size="large"
          fullWidth={true}
          label={translations?.subscriptionActivation?.activationActualProfileButton || 'Activate with this account'}
          onClick={handleUserCardButtonClick}
          isDisabled={loading}
        />
        <Button
          className=""
          type="secondary"
          size="large"
          fullWidth={true}
          label={translations?.subscriptionActivation?.logoutButton || 'Use another account (log out)'}
          onClick={() => setLogoutModalOpen(true)}
        />
      </div>
    </div>
  );

  // JSX markup for the address form state
  const addressFormMarkup = (
    <div className="print-subscription print-subscription--address-new">
      <ActivationHeader state={state} codeInfo={codeInfo} />
      <AddressForm
        onButtonClick={handleAddressFormSubmit}
        addressData={printAddressData}
        updateSnackbarMessage={updateSnackbarMessage}
      />
    </div>
  );

  // JSX markup for the address card state
  const addressCardMarkup = (
    <div className="print-subscription print-subscription--address-exist">
      <ActivationHeader state={state} codeInfo={codeInfo} />
      <div className="step-address__form">
        <AddressCard addressData={printAddressData} />
        <div className="step-address__link">
          <button className="link link--medium" onClick={handleEditAddressClick} style={{ cursor: 'pointer' }}>
            {translations?.subscriptionActivation?.editAddress || 'Edit delivery address'}
          </button>
        </div>
        <div className="input-group button-group">
          <Button
            className="step-address__button"
            size="large"
            type="primary"
            fullWidth={true}
            label={translations?.subscriptionActivation?.activationButton || 'Subscription Activation'}
            onClick={handleAddressCardButtonClick}
            isDisabled={loading}
          />
        </div>
      </div>
    </div>
  );

  // JSX markup for the invalid code state
  const invalidCodeMarkup = (
    <div>
      <svg
        width="446"
        height="140"
        viewBox="0 0 446 140"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ maxWidth: '100%' }}
      >
        <g clip-path="url(#clip0_6571_117381)">
          <rect width="445" height="140" transform="translate(0.5)" fill="white" />
          <mask
            id="mask0_6571_117381"
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="826"
            height="140"
          >
            <rect x="0.5" width="825" height="140" fill="white" />
          </mask>
          <g mask="url(#mask0_6571_117381)">
            <circle cx="223" cy="69.5" r="384" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="354" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="324" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="294" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="264" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="234" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="204" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="174" fill="white" stroke="#E2E8F0" />
            <circle cx="223" cy="69.5" r="144" fill="white" stroke="#E2E8F0" />
            <circle cx="223.5" cy="70" r="113.5" fill="white" stroke="#E2E8F0" />
            <circle cx="223.5" cy="70" r="83.5" fill="white" stroke="#E2E8F0" />
            <circle cx="223.5" cy="70" r="53.5" fill="white" stroke="#E2E8F0" />
            <rect
              x="0.5"
              width="445"
              height="140"
              fill="url(#paint0_radial_6571_117381)"
              style={{ mixBlendMode: 'overlay' }}
            />
            <circle cx="111.5" cy="70" r="19.5" fill="white" stroke="#E2E8F0" />
            <path
              d="M108.501 69V68.8632C108.501 67.5056 108.435 65.9924 109.204 65.0208C109.643 64.4667 110.353 64 111.5 64C112.647 64 113.357 64.4667 113.795 65.0208C114.565 65.9924 114.499 67.5056 114.499 68.8632V69M106.5 69.8235C106.5 69.3687 106.5 69 106.5 69H116.5V74.3529C116.5 75.2626 116.5 76 116.5 76H106.5C106.5 76 106.5 75.2626 106.5 74.3529V69.8235Z"
              stroke="#475569"
              stroke-width="1.5"
              stroke-linecap="square"
            />
            <circle cx="335.5" cy="70" r="19.5" fill="white" stroke="#E2E8F0" />
            <path
              d="M329.5 69.375V65H333.7L335.8 66.875H341.5V69.375M329.5 69.375V75H341.5V69.375M329.5 69.375H341.5"
              stroke="#475569"
              stroke-width="1.5"
              stroke-linecap="square"
            />
            <g filter="url(#filter0_dd_6571_117381)">
              <circle cx="223.5" cy="70" r="32" fill="#BE185D" />
            </g>
          </g>
          <path
            d="M223.5 61.25V71.75M223.5 78.7675L223.518 78.7481M241 70C241 79.665 233.165 87.5 223.5 87.5C213.835 87.5 206 79.665 206 70C206 60.335 213.835 52.5 223.5 52.5C233.165 52.5 241 60.335 241 70Z"
            stroke="white"
            stroke-width="1.5"
            stroke-linecap="square"
          />
        </g>
        <defs>
          <filter
            id="filter0_dd_6571_117381"
            x="171.5"
            y="38"
            width="104"
            height="104"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology radius="5" operator="erode" in="SourceAlpha" result="effect1_dropShadow_6571_117381" />
            <feOffset dy="20" />
            <feGaussianBlur stdDeviation="12.5" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6571_117381" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology radius="5" operator="erode" in="SourceAlpha" result="effect2_dropShadow_6571_117381" />
            <feOffset dy="10" />
            <feGaussianBlur stdDeviation="5" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_6571_117381" result="effect2_dropShadow_6571_117381" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_6571_117381" result="shape" />
          </filter>
          <radialGradient
            id="paint0_radial_6571_117381"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(223 70) scale(198.767 70)"
          >
            <stop stop-color="#020617" />
            <stop offset="1" stop-color="white" />
          </radialGradient>
          <clipPath id="clip0_6571_117381">
            <rect width="445" height="140" fill="white" transform="translate(0.5)" />
          </clipPath>
        </defs>
      </svg>
      <h2 className="page-subscription-activation__title">
        {translations?.subscriptionActivation?.activationFailedTitle || 'Subscription activation failed'}
      </h2>
      <div className="page-subscription-activation__description">
        {translations?.subscriptionActivation?.activationFailedDescription ||
          'Unfortunately, subscription activation failed. Please try again.'}
      </div>
      <div className="page-subscription-activation__description" style={{ marginBottom: '40px' }}>
        {translations?.subscriptionActivation?.activationFailedInstructions ||
          'If the problem persists, please contact our customer service.'}
      </div>
      <div style={{ marginBottom: '40px' }}>
        <Button
          className=""
          type="secondary"
          size="large"
          fullWidth={true}
          label={translations?.subscriptionActivation?.activationFailedButton || 'Go to Forbes.cz'}
          href="/"
        />
      </div>
    </div>
  );

  // Main render function
  return (
    <Fragment>
      <div className="page-subscription-activation__content">
        <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => handleSnackbarClose()} />
        {renderState()}
        <div className="page-subscription-activation__footer">
          <Footer failed={false} />
        </div>
      </div>
      <Dialog isOpen={logoutModalOpen} onClose={() => setLogoutModalOpen(false)}>
        <div className="modal modal--small login-modal">
          <div className="modal__wrapper">
            <div className="modal__header login-modal__header">
              <h5>{translations?.subscriptionActivation?.logoutModalTitle || 'Logout'}</h5>
              <button
                className="login-modal__close-button"
                onClick={() => setLogoutModalOpen(false)}
                aria-label="Close modal"
              >
                <Icon name="close" size="small" />
              </button>
            </div>

            <div className="login-modal__content modal__content">
              {translations?.subscriptionActivation?.logoutModalDescription ||
                'You will be logged out from your account. Do you want to continue?'}
            </div>

            <div className="modal__footer login-modal__footer" style={{ justifyContent: 'flex-start', gap: '16px' }}>
              <button
                style={{ fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => setLogoutModalOpen(false)}
              >
                {translations?.subscriptionActivation?.logoutModalCancel || 'Cancel'}
              </button>
              <button
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'var(--color-link-status-alert-default)',
                }}
                onClick={handleLogout}
              >
                {translations?.subscriptionActivation?.logoutModalConfirm || 'Logout'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};

export default SubscriptionActivation;
